<script lang="ts" setup>
/**
 * We show a preview of the file, where you can edit it before we send it to the server
 * we use the cropperjs library for this
 *
 * @see https://fengyuanchen.github.io/cropperjs/
 *
 * prerequistes
 * @see https://www.npmjs.com/package/@types/vue-cropperjs
 * npm install --save @types/vue-cropperjs
 *
 * @see https://www.npmjs.com/package/vue-cropperjs
 * npm install --save vue-cropperjs
 */
import VueCropper, { type VueCropperMethods, type VueCropperProps } from 'vue-cropperjs';
import { onMounted, ref, watch } from 'vue';
import { AlertMessage, ButtonComp, ContentComp, IconComp, InputSelect } from '@/components';
import { $tm } from '@/locize';
import type { SelectLine } from '@/objects';

// Import the styles
import 'cropperjs/dist/cropper.css';

/**
 * The props we accept
 */
const props = withDefaults(
    defineProps<{
        imagedata: string;

        editeddata?: string | null;

        /**
         * The aspect ratio of the cropper
         * @default 1
         */
        aspectRatio: number;

        /**
         * The aspect ratios we can select
         */
        aspectRatios?: SelectLine[] | null;

        /**
         * The height of the image we will return
         * Note: this is the height of the preview image, but the cropped data will be based on the original size. It will be resized to this height in the backend
         * @default 100
         */
        imageheight?: number | null;
    }>(),
    {
        imageheight: 100,
        aspectRatios: null,
    },
);

const emit = defineEmits<{
    (e: 'update:editeddata', val: string): void;
}>();

/**
 * The cropper instance
 */
const cropper = ref<(VueCropperProps & VueCropperMethods) | null>(null);

/**
 * The aspect ratio we are using
 */
const aspectvalue = defineModel<number>('aspectRatio', { required: true });

/**
 * Internal value to hold the data. We use this to update the cropper since it has to be set after the cropper is ready
 * This is the original image and will never be changed internally
 */
const data = ref<string | null>(null);

/**
 * The preview image
 * This is the data we will return to the parent after it has been cropped
 */
const previewBase64 = ref<string | null>(props.editeddata ?? null);

/**
 * This holds the zoom value.
 * We dont really use this yet since the cropper support scroll by mouse wheel. The idea is to use it for a slider in the future
 */
const zoom = ref<HTMLInputElement | null>(null);

const zoommodel = ref<string>('0');

/**
 * When we zoom the image
 */
const onZoomrangeChanged = () => {
    if (originalZeroRatio === null) {
        return;
    }
    const currentZoomValue: number = parseInt(zoommodel.value) ?? 0;
    const stepvalue = originalZeroRatio! * 0.1; // We step 10% of the original value either way
    const newvalue = originalZeroRatio! + currentZoomValue * stepvalue;

    // Do the zoom
    cropper.value?.zoomTo(newvalue);

    updateEditedData();
};

let originalZeroRatio: number | null = null;

/**
 * Triggered when the cropperjs does the zoom
 * @param e
 */
const onZoom = (e: any) => {
    // We normalize this one to rangesteps and set the value we do that by finding how many % of the original value we are
    if (originalZeroRatio === null) {
        return;
    }

    const zoomvalue = Math.round((e.detail.ratio - originalZeroRatio) / (originalZeroRatio * 0.1));
    zoommodel.value = zoomvalue.toString();

    updateEditedData();
};

/**
 * Set the zoom value
 * @param value
 */
const setZoom = (value: number) => {
    zoommodel.value = value.toString();
    onZoomrangeChanged();
};

/**
 * When the cropper is ready
 * We set initial values
 */
const onReady = () => {
    setTimeout(() => {
        // TODO: Think the issue here is that the Modal is not properly mounted yet
        data.value = props.imagedata;
        setTimeout(() => {
            updateEditedData();
            limitCropbox();

            // Set original zero ratio
            const canvasdata = cropper.value?.getCanvasData();
            originalZeroRatio = canvasdata?.width! / canvasdata?.naturalWidth!;
        }, 100);
    }, 100);
};

/**
 * When we press the reset button
 */
const onReset = () => {
    cropper.value?.reset();
    updateEditedData();
};

/**
 * Helper properties to show the preview image
 */
const previewHeight = ref<number | null>(props.imageheight);
const previewWidth = ref<number | null>(props.imageheight == null ? 0 : props.imageheight * aspectvalue.value);

/**
 * This will set the preview size based on the aspect ratio
 * We will limit the width to 500
 */
const updatePreviewSize = () => {
    let canvasData = cropper.value?.getCanvasData();
    let aspect = aspectvalue.value;
    let height: number = aspect === 0 ? (canvasData?.height ?? 0) : (props.imageheight ?? 0);
    let width: number = aspect === 0 ? (canvasData?.width ?? 0) : height * aspect;

    if (aspect === 0) {
        let cropbox = cropper.value?.getCropBoxData();
        previewWidth.value = cropbox?.width ?? 0;
        previewHeight.value = props.imageheight ?? cropbox?.height ?? 0;
    } else {
        previewWidth.value = width > 500 ? 500 : width;
        previewHeight.value = previewWidth.value / aspect;
    }
};

/**
 * Make sure the cropbox obides by the rules we set
 * We want to limit the width to 500 if we dont have an aspect ratio and we want to keep the height
 */
const limitCropbox = () => {
    if (aspectvalue.value === 0) {
        let cropbox = cropper.value?.getCropBoxData();

        let width = (cropbox?.width ?? 0) > 500 ? 500 : (cropbox?.width ?? 0);

        if (props.imageheight != null) {
            cropper.value?.setCropBoxData({
                left: cropbox?.left ?? 0,
                top: cropbox?.top ?? 0,
                width: width,
                height: props.imageheight,
            });
        }
    }
};

/**
 * Helper to update the aspect ratio
 */
watch(
    () => aspectvalue.value,
    (val) => {
        cropper.value?.setAspectRatio(val);
        updateEditedData();
    },
);

/**
 * When we have croped the image or manipulated the crop area
 */
const updateEditedData = () => {
    if (cropper.value != null) {
        previewBase64.value = cropper.value?.getCroppedCanvas().toDataURL('image/png');
        updatePreviewSize();
        emit('update:editeddata', previewBase64.value ?? '');
    }
};

onMounted(() => {
    onReady();
});
</script>

<template>
    <ContentComp>
        <div class="imagehandlereditor d-flex flex-column gap-3">
            <div class="position-relative">
                <VueCropper
                    v-if="data != null"
                    :src="data"
                    ref="cropper"
                    :aspect-ratio="aspectvalue"
                    dragmode="move"
                    @zoom="onZoom"
                    @cropend="updateEditedData"
                    @cropmove="limitCropbox"
                />
                <div class="position-absolute top-0 end-0">
                    <ButtonComp
                        :title="$tm('general.reset')"
                        button-type="Secondary"
                        @click="onReset"
                        class="m-2 opacity-75"
                        ><IconComp icon="fa-rotate-right"
                    /></ButtonComp>
                </div>
            </div>
            <div
                class="d-flex gap-3"
                v-if="true"
            >
                <ButtonComp
                    icon="fa-magnifying-glass-minus"
                    button-type="Custom"
                    @click="setZoom(parseInt(zoommodel) - 1)"
                />
                <input
                    v-model="zoommodel"
                    type="range"
                    ref="zoom"
                    min="-10"
                    max="10"
                    step="1"
                    class="form-range flex-grow-1"
                    @change="onZoomrangeChanged"
                />
                <ButtonComp
                    icon="fa-magnifying-glass-plus"
                    button-type="Custom"
                    @click="setZoom(parseInt(zoommodel) + 1)"
                />
            </div>
            <div class="d-flex gap-5">
                <div class="flex-grow-1 d-flex flex-column gap-2">
                    <label>{{ $tm('imagehandler.preview') }}</label>

                    <img
                        v-if="previewBase64 != null"
                        class="border cropper-bg"
                        :class="[aspectvalue === 0 ? 'preview-max-height' : '']"
                        :src="previewBase64"
                        alt="preview"
                        :style="{ height: previewHeight + 'px', maxWidth: previewWidth + 'px' }"
                    />
                </div>
                <div
                    v-if="Array.isArray(aspectRatios)"
                    class="d-flex flex-column gap-2"
                >
                    <label>{{ $tm('general.aspectratio') }}</label>
                    <InputSelect
                        v-model="aspectvalue"
                        :datasource="{ source: aspectRatios }"
                    />
                </div>
            </div>
        </div>
        <AlertMessage
            class="mt-3"
            dismisskey="imagehandler.tipsandtricks"
            :show-icon="false"
            type="info"
            ><div v-html="$tm('imagehandler.tipsandtricks', '', true)"></div
        ></AlertMessage>
    </ContentComp>
</template>
